import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import apiClient from '../utils/api';
import DOMPurify from 'dompurify';

/**
 * Generic renderer for the public /features/* landing pages.
 *
 * Content is authored once in the backend (services/feature_pages.py) and
 * fetched here by slug, so the copy is server-rendered into the HTML shell for
 * crawlers AND rendered live for JS users from the same source. The body uses
 * the shared visual classes from src/index.css (feature-grid, cta-primary, ...);
 * those divs already carry `not-prose` so `prose` typography doesn't override
 * their layout.
 */
const FeaturePage = ({ slug }) => {
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setPage(null);
    setError('');
    apiClient
      .get(`/api/pages/${slug}`)
      .then((res) => { if (active) setPage(res.data); })
      .catch(() => { if (active) setError('Page not found.'); });
    return () => { active = false; };
  }, [slug]);

  // Keep the tab title + meta description correct during client-side navigation
  // (the server already injects them into the initial HTML for crawlers).
  useEffect(() => {
    if (page?.title) document.title = page.title;
    return () => { document.title = 'Vegetablah'; };
  }, [page?.title]);

  useEffect(() => {
    if (page?.description) {
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = page.description;
    }
  }, [page?.description]);

  return (
    <div className="mx-auto max-w-[1080px] px-4 sm:px-6 lg:px-10">
      {!page && !error && (
        <div className="text-center py-12">
          <p className="text-fg-3">Loading…</p>
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <p className="text-off mb-4">{error}</p>
              <Link to="/"><Button>Back home</Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      {page && (
        <Card className="border-0 bg-transparent">
          <CardContent className="p-0">
            <div
              className="prose prose-lg max-w-none dark:prose-invert prose-headings:font-medium prose-headings:tracking-[-0.02em] prose-headings:text-fg prose-p:text-fg-2 prose-li:text-fg-2 prose-strong:font-medium prose-strong:text-fg prose-a:text-fg"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(page.html, { ADD_ATTR: ['target', 'rel'] }),
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FeaturePage;
